import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  commanderAgent,
  getAgentTimeline,
  reevaluerEligibiliteAgent,
} from "../../../../../services/graal-api/leadAutoAgentService";
import { agentTimelineAtom } from "./agentTimelineAtom";
import {
  agentActif,
  boutonsDisponibles,
  compagnieParDefaut,
  etatAgentInfo,
  formatDuree,
  formatHeure,
  formatHeureSeconde,
  ICONES_TYPES,
  lancements,
  libelleCommande,
  libelleCourtEtat,
  motifAttente,
  NIVEAUX,
  nomCompagnie,
  resumeArret,
  resumeDetail,
  sectionsParCompagnie,
  titreLancement,
} from "./agentTimelineHelpers";

// Cadence du rafraîchissement tant que l'agent travaille (PER-2433).
const INTERVALLE_ACTIF_MS = 10000;

/**
 * Timeline de l'agent de tarification sur une demande auto (PER-2433), une
 * compagnie à la fois (PER-2501) : un sélecteur de compagnie en tête (une
 * pastille par compagnie du référentiel de graal, avec son état), puis la
 * carte de décision de la compagnie choisie — état, dernier événement (où
 * l'agent s'est arrêté), commande en cours, boutons — qui reste collée en
 * haut pendant le défilement, puis ses événements du plus récent au plus
 * ancien, regroupés par lancement. graal est la seule source : rien n'est
 * déduit côté back-office.
 */
const LeadAutoAgentTimeline = ({ leadAutoItem, onRefreshLead, canCommand = true }) => {
  const [timeline, setTimeline] = useState(null);
  // Partagée avec la barre d'accès de la carte et les onglets du panneau (pastille « 2 compagnies »).
  const [, setTimelinePartagee] = useAtom(agentTimelineAtom);
  const [isLoading, setIsLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  // Commande en cours d'envoi, « compagnie:type » (null sinon) : tous les boutons sont désactivés
  // pendant l'appel, seul celui qui a été cliqué montre le spinner (revue du 06/10, deux boutons possibles).
  const [enCours, setEnCours] = useState(null);
  // Compagnie choisie par l'équipe ; null = celle de l'état le plus urgent (PER-2501).
  const [compagnieChoisie, setCompagnieChoisie] = useState(null);
  // Lancements dépliés ou repliés à la main ; sans choix, le plus récent et ceux en cours sont dépliés.
  const [deplies, setDeplies] = useState({});
  const leadUuid = leadAutoItem?.id;
  const minuteur = useRef(null);
  const dernierEtatRafraichi = useRef(null);
  const refContenu = useRef(null);

  const charger = useCallback(async () => {
    if (!leadUuid) return;
    try {
      const { data } = await getAgentTimeline(leadUuid);
      setTimeline(data);
      setTimelinePartagee(data);
      setErreur(null);
    } catch (e) {
      setErreur("La timeline de l'agent n'a pas pu être chargée.");
    } finally {
      setIsLoading(false);
    }
  }, [leadUuid, setTimelinePartagee]);

  useEffect(() => {
    setIsLoading(true);
    charger();
  }, [charger]);

  // Le badge de la fiche et de la liste vient de graal via la recherche : quand la
  // timeline voit un autre état que celui de la fiche, on recharge le lead (une fois
  // par état, pour ne pas boucler si les deux sources divergeaient durablement).
  const etatTimeline = timeline ? timeline.etat || null : undefined;
  const etatFiche = leadAutoItem?.agent?.etat || null;
  useEffect(() => {
    if (etatTimeline === undefined || !onRefreshLead) return;
    if (etatTimeline !== etatFiche && dernierEtatRafraichi.current !== etatTimeline) {
      dernierEtatRafraichi.current = etatTimeline;
      onRefreshLead();
    }
  }, [etatTimeline, etatFiche, onRefreshLead]);

  // Rafraîchissement automatique tant qu'une compagnie est active ; arrêt sinon.
  useEffect(() => {
    clearInterval(minuteur.current);
    if (agentActif(timeline)) {
      minuteur.current = setInterval(charger, INTERVALLE_ACTIF_MS);
    }
    return () => clearInterval(minuteur.current);
  }, [timeline, charger]);

  // Verdict redemandé à l'agent chez une compagnie (PER-2449) : pas une commande, pas de confirmation.
  const reevaluer = async (section) => {
    setEnCours(`${section.compagnie}:reevaluer`);
    try {
      const { data } = await reevaluerEligibiliteAgent(leadUuid, section.compagnie);
      const nom = nomCompagnie(data?.compagnie || section.compagnie, timeline?.compagnies);
      if (data?.eligible) {
        toast.success(`Demande éligible chez ${nom} : l'agent peut être lancé.`);
      } else {
        toast.warn(`Demande toujours non éligible chez ${nom}.`);
      }
      await charger();
      if (onRefreshLead) onRefreshLead();
    } catch (e) {
      toast.error(
        e?.response?.data?.message ||
          (e?.response?.status === 503
            ? "L'agent est injoignable : réessayer dans un instant."
            : "La vérification n'a pas pu être demandée."),
      );
    } finally {
      setEnCours(null);
    }
  };

  const commander = async (section, bouton) => {
    if (bouton.type === "reevaluer") return reevaluer(section);
    if (!window.confirm(`${bouton.confirmation}\n\nCompagnie : ${section.nom}.`)) return;
    setEnCours(`${section.compagnie}:${bouton.type}`);
    try {
      await commanderAgent(
        leadUuid,
        {
          type: bouton.type,
          ...(bouton.interrupteur ? { interrupteur: bouton.interrupteur } : {}),
        },
        section.compagnie,
      );
      // graal enregistre la commande puis la transmet hors requête : ne pas promettre plus que ce qu'il a fait.
      toast.success(`Commande « ${bouton.label} » enregistrée chez ${section.nom} : l'agent la prend en compte d'ici une minute.`);
      await charger();
      if (onRefreshLead) onRefreshLead();
    } catch (e) {
      const message =
        e?.response?.data?.message ||
        (e?.response?.status === 409
          ? `La commande n'a pas de sens dans l'état actuel de la demande chez ${section.nom}.`
          : "La commande n'a pas pu être enregistrée.");
      toast.error(message);
      await charger();
    } finally {
      setEnCours(null);
    }
  };

  // Changer de compagnie ramène en haut du panneau : la carte de décision de l'autre compagnie se lit d'abord.
  const choisirCompagnie = (compagnie) => {
    setCompagnieChoisie(compagnie);
    setDeplies({});
    const defilant = refContenu.current?.closest(".la-panneau__contenu");
    if (defilant) defilant.scrollTop = 0;
  };

  if (isLoading && !timeline) {
    return (
      <Container>
        <i className="fa fa-spinner fa-spin mr-2" /> Chargement de la timeline de l'agent…
      </Container>
    );
  }

  const sections = sectionsParCompagnie(timeline);
  const compagnieAffichee = sections.some((s) => s.compagnie === compagnieChoisie)
    ? compagnieChoisie
    : compagnieParDefaut(sections, timeline?.compagnie);
  const section = sections.find((s) => s.compagnie === compagnieAffichee) || null;

  return (
    <Container ref={refContenu}>
      {erreur && (
        <Alerte color="#dc2626" role="alert">
          <i className="fa fa-exclamation-triangle mr-2" />
          {erreur}
        </Alerte>
      )}

      {!leadAutoItem?.isFinalized && (
        <Alerte color="#6c757d">
          <i className="fa fa-info-circle mr-2" />
          La demande n'est pas finalisée : l'agent ne peut pas la traiter tant que la seconde
          étape du formulaire n'est pas terminée.
        </Alerte>
      )}

      <SelecteurCompagnies sections={sections} choisie={compagnieAffichee} onChoisir={choisirCompagnie} />

      {section && (
        <CompagnieChoisie
          key={section.compagnie}
          id={`agent-${leadUuid}-${section.compagnie}`}
          section={section}
          boutons={canCommand ? boutonsDisponibles(section, leadAutoItem) : []}
          enCours={enCours}
          onCommander={(bouton) => commander(section, bouton)}
          deplies={deplies}
          onDeplier={(idLancement, ouvert) => setDeplies((d) => ({ ...d, [idLancement]: ouvert }))}
          actif={agentActif(timeline)}
          onRafraichir={charger}
        />
      )}
    </Container>
  );
};

/**
 * Sélecteur de compagnie (PER-2501) : une pastille par compagnie du
 * référentiel avec son état court ; flèches gauche et droite au clavier.
 */
const SelecteurCompagnies = ({ sections, choisie, onChoisir }) => {
  const surClavier = (evenement, index) => {
    if (evenement.key !== "ArrowRight" && evenement.key !== "ArrowLeft") return;
    evenement.preventDefault();
    const suivant = sections[(index + (evenement.key === "ArrowRight" ? 1 : -1) + sections.length) % sections.length];
    onChoisir(suivant.compagnie);
    const bouton = document.getElementById(`agent-compagnie-${suivant.compagnie}`);
    if (bouton) bouton.focus();
  };

  return (
    <Selecteur>
      <div role="tablist" aria-label="Compagnie">
        {sections.map((s, index) => {
          const etat = etatAgentInfo(s.etat);
          const muette = !s.sollicitee && !s.actif;
          return (
            <Pastille
              key={s.compagnie}
              type="button"
              role="tab"
              id={`agent-compagnie-${s.compagnie}`}
              aria-selected={s.compagnie === choisie}
              aria-controls={`agent-compagnie-${s.compagnie}-contenu`}
              tabIndex={s.compagnie === choisie ? 0 : -1}
              $muette={muette}
              onClick={() => onChoisir(s.compagnie)}
              onKeyDown={(e) => surClavier(e, index)}
            >
              <span>{s.nom}</span>
              {s.etat ? (
                <EtatCourt color={etat.color}>
                  <i className={`fa ${etat.icon}`} aria-hidden="true" />
                  {libelleCourtEtat(s.etat)}
                </EtatCourt>
              ) : (
                <EtatCourt>{muette ? "non sollicitée" : "aucune intervention"}</EtatCourt>
              )}
            </Pastille>
          );
        })}
      </div>
    </Selecteur>
  );
};

/**
 * La compagnie choisie : sa carte de décision (collée en haut, avec le
 * rafraîchissement dans son en-tête), puis ses lancements du plus récent au
 * plus ancien.
 */
const CompagnieChoisie = ({ id, section, boutons, enCours, onCommander, deplies, onDeplier, actif, onRafraichir }) => {
  const etat = etatAgentInfo(section.etat);
  const commande = section.commandeEnCours;
  const commandeOuverte = Boolean(commande && ["en_attente", "transmise"].includes(commande.statut));
  // Refus prononcé par l'agent (revue du 06/10) : graal ne garde en « commandeEnCours » que les commandes ouvertes,
  // la dernière commande, tout statut, dit le refus et son motif. Rien à montrer tant qu'une commande est ouverte.
  const refus = section.derniereCommande?.statut === "refusee" && !commandeOuverte ? section.derniereCommande : null;
  const regenererPropose = boutons.some((b) => b.type === "regenerer") && boutons.length > 1;
  // Arrêt sur un préalable (PER-2458) : l'agent dit ce qu'un humain doit faire d'abord, le bouton le relance ensuite.
  // Le motif se lit aussi sans droit de commande : savoir pourquoi l'agent attend ne dépend pas de pouvoir le relancer.
  const prealable = motifAttente(section);
  // Où l'agent s'est arrêté : son dernier événement, tel qu'il l'a écrit.
  const arret = resumeArret(section);
  const muette = !section.sollicitee && !section.actif;
  const groupes = lancements(section.evenements);
  const cle = (type) => `${section.compagnie}:${type}`;

  const aide = (() => {
    if (prealable) {
      return `L'agent s'est arrêté : ${prealable.motif}. ${prealable.explication || "Ce bouton relance l'agent."}${
        section.etatDetail === "envoi_prospect" ? " Il reprend le devis existant, il n'en refait pas." : ""
      }`;
    }
    if (section.etat === "attente_action") {
      return section.interrupteurs?.[section.etatDetail] === true
        ? "L'interrupteur a été ouvert depuis l'arrêt : l'agent ne repart pas seul, le bouton le relance ici."
        : `Interrupteur fermé chez ${section.nom} : le bouton relance l'agent ici. L'ouvrir dans Paramètres › Agent de tarification le laisse enchaîner seul sur les prochaines demandes.`;
    }
    if (regenererPropose) {
      return "« Lancer l'agent » reprend le devis existant et l'envoie s'il ne l'a pas été ; « Régénérer le devis » en produit un nouveau à partir des réponses actuelles de la demande.";
    }
    return "";
  })();

  const actions = (boutons.length > 0 || aide) && (
    <Actions>
      {boutons.map((bouton) => (
        <button
          key={bouton.label}
          type="button"
          className={`btn btn-sm ${bouton.className}`}
          disabled={Boolean(enCours)}
          onClick={() => onCommander(bouton)}
        >
          <i className={`fa ${enCours === cle(bouton.type) ? "fa-spinner fa-spin" : bouton.icon} mr-2`} aria-hidden="true" />
          {bouton.label}
        </button>
      ))}
      {aide && <Aide>{aide}</Aide>}
    </Actions>
  );

  return (
    <div id={`agent-compagnie-${section.compagnie}-contenu`} role="tabpanel" aria-labelledby={`agent-compagnie-${section.compagnie}`}>
      <Carte aria-labelledby={`${id}-titre`}>
        <TeteCarte>
          <TeteGauche>
            <TitreCompagnie id={`${id}-titre`}>{section.nom}</TitreCompagnie>
            {section.etat && (
              <EtatPill color={etat.color} title={section.etatDetail || ""}>
                <i className={`fa ${etat.icon} mr-2`} aria-hidden="true" />
                {etat.label}
              </EtatPill>
            )}
            {!section.actif && !muette && (
              <small className="text-muted" title="graal ne lance pas cette compagnie seul à la finalisation d'une demande">
                lancement manuel seulement
              </small>
            )}
            {section.majLe && <small className="text-muted">Mis à jour {formatHeure(section.majLe)}</small>}
          </TeteGauche>
          {/* Tant que l'agent travaille, l'icône tourne : la timeline se rafraîchit seule toutes les dix secondes. */}
          <button
            type="button"
            className="btn btn-sm btn-default"
            onClick={onRafraichir}
            title={actif ? "Actualisation automatique toutes les dix secondes. Rafraîchir maintenant" : "Rafraîchir la timeline"}
            aria-label={actif ? "Rafraîchir la timeline (actualisation automatique en cours)" : "Rafraîchir la timeline"}
          >
            <i className={`fa fa-refresh${actif ? " fa-spin" : ""}`} aria-hidden="true" />
          </button>
        </TeteCarte>

        {muette ? (
          <Arret color={etat.color}>
            <i className={`fa ${etat.icon}`} aria-hidden="true" />
            <span>
              Cette compagnie n'est pas sollicitée automatiquement à la finalisation des demandes : l'agent n'y est jamais
              intervenu sur celle-ci.{boutons.length > 0 ? " Il peut y être lancé à la main." : ""}
            </span>
          </Arret>
        ) : arret ? (
          <Arret color={(NIVEAUX[arret.niveau] || NIVEAUX.ok).color}>
            <i className={`fa ${ICONES_TYPES[arret.type] || "fa-circle"}`} aria-hidden="true" />
            <span>
              <strong>{arret.libelle}</strong>
              {arret.texte && <> : {arret.texte}</>}
              {arret.survenuLe && <small className="text-muted text-nowrap ml-2">{formatHeure(arret.survenuLe)}</small>}
            </span>
          </Arret>
        ) : (
          <Arret color={etat.color}>
            <i className={`fa ${etat.icon}`} aria-hidden="true" />
            <span>L'agent n'est jamais intervenu sur cette demande chez {section.nom}.</span>
          </Arret>
        )}

        {commandeOuverte && (
          <Alerte color="#0d6efd">
            <i className="fa fa-paper-plane mr-2" aria-hidden="true" />
            Commande « {libelleCommande(commande)} »{" "}
            {commande.statut === "transmise" ? "transmise à l'agent" : "en attente de l'agent"}
            {commande.auteur ? ` (par ${commande.auteur})` : " (déclenchement automatique)"} — la suite
            apparaîtra ici.
          </Alerte>
        )}
        {refus && (
          <Alerte color="#dc2626">
            <i className="fa fa-times-circle mr-2" aria-hidden="true" />
            Dernière commande « {libelleCommande(refus)} » refusée par l'agent : {refus.motifRefus || "sans motif"}.
          </Alerte>
        )}

        {actions}
      </Carte>

      {/* Sans événement, la carte a déjà dit que l'agent n'est jamais intervenu : rien de plus à montrer. */}
      {groupes.map((groupe, index) => {
        const ouvert = groupe.id in deplies ? deplies[groupe.id] : index === 0 || groupe.enCours;
        return (
          <Lancement key={groupe.id} groupe={groupe} id={`${id}-l-${index}`} ouvert={ouvert} onDeplier={(o) => onDeplier(groupe.id, o)} />
        );
      })}
    </div>
  );
};

/** Un lancement : en-tête repliable (début, durée, issue) et ses événements du plus récent au plus ancien. */
const Lancement = ({ groupe, id, ouvert, onDeplier }) => {
  const issue = etatAgentInfo(groupe.etat);
  return (
    <Groupe>
      <TeteGroupe type="button" aria-expanded={ouvert} aria-controls={id} onClick={() => onDeplier(!ouvert)}>
        <i className={`fa ${ouvert ? "fa-chevron-down" : "fa-chevron-right"}`} aria-hidden="true" />
        <strong>{titreLancement(groupe)}</strong>
        {groupe.type === "lancement" && <small className="text-muted">{formatDuree(groupe.dureeMs)}</small>}
        {groupe.etat && (
          <Issue color={issue.color}>
            <i className={`fa ${issue.icon}`} aria-hidden="true" />
            {libelleCourtEtat(groupe.etat) || issue.label}
          </Issue>
        )}
      </TeteGroupe>
      {ouvert && (
        <Liste id={id}>
          {groupe.evenements.map((e) => {
            const couleur = (NIVEAUX[e.niveau] || NIVEAUX.ok).color;
            const detail = resumeDetail(e);
            return (
              <Evenement key={e.uuid}>
                <Heure>{formatHeureSeconde(e.survenuLe)}</Heure>
                <Puce color={couleur}>
                  <i className={`fa ${ICONES_TYPES[e.type] || "fa-circle"}`} aria-hidden="true" />
                </Puce>
                <Corps>
                  <strong>{e.libelle}</strong>
                  {detail && <Detail>{detail}</Detail>}
                  {e.explication && (
                    <Explication color={couleur}>
                      <i className="fa fa-lightbulb-o mr-2" aria-hidden="true" />
                      {e.explication}
                    </Explication>
                  )}
                </Corps>
              </Evenement>
            );
          })}
        </Liste>
      )}
    </Groupe>
  );
};

const Container = styled.div`
  padding: 0 0 12px;
`;
// Sur un écran étroit, les pastilles restent sur une ligne qui défile : deux
// compagnies tiennent dans 320 px, une troisième se révèle au doigt.
const Selecteur = styled.div`
  padding: 4px 0 10px;

  > [role="tablist"] {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  @media (max-width: 767px) {
    > [role="tablist"] {
      flex-wrap: nowrap;
      overflow-x: auto;
      padding-bottom: 2px;
    }
  }
`;
const Pastille = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 0 12px;
  border: 1px solid var(--ds-slate-300);
  border-radius: 999px;
  background: white;
  color: ${({ $muette }) => ($muette ? "var(--ds-slate-500)" : "var(--ds-slate-700)")};
  font: inherit;
  font-size: 13px;
  font-weight: ${({ $muette }) => ($muette ? 500 : 600)};
  white-space: nowrap;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease;

  &:hover {
    background: var(--ds-slate-50);
  }
  &[aria-selected="true"] {
    border-color: var(--ds-blue);
    background: var(--ds-blue-soft);
    color: var(--ds-blue);
  }
  &:focus-visible {
    outline: 2px solid var(--ds-blue);
    outline-offset: 2px;
  }
  @media (max-width: 767px) {
    flex: none;
    min-height: 44px;
    padding: 0 10px;
    font-size: 12.5px;
  }
`;
const EtatCourt = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
  color: var(--ds-slate-600);

  > .fa {
    font-size: 11px;
    color: ${({ color }) => color || "var(--ds-slate-500)"};
  }
  [aria-selected="true"] > & {
    color: var(--ds-blue);
  }
`;
// La carte de décision déborde du rembourrage du panneau (16 px en haut, 20 px
// sur les côtés) pour couvrir toute sa largeur, collée au bord haut du panneau
// pendant le défilement.
const Carte = styled.section`
  position: sticky;
  top: -16px;
  z-index: 2;
  margin: 0 -20px 12px;
  padding: 10px 20px 12px;
  background: white;
  border-bottom: 1px solid var(--ds-slate-200);
  box-shadow: 0 6px 10px -8px rgba(15, 23, 42, 0.25);
`;
// En-tête de la carte : à gauche le nom, l'état et la mise à jour (qui
// peuvent passer à la ligne), à droite le rafraîchissement, sur l'axe du titre.
const TeteCarte = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px 12px;
  margin-bottom: 8px;

  > .btn {
    flex: none;
    margin-left: auto;
  }
`;
const TeteGauche = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  min-width: 0;
`;
// Même hauteur que le bouton de rafraîchissement (31 px) : le titre et
// l'action restent sur le même axe, même quand l'en-tête passe à la ligne.
const TitreCompagnie = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 31px;
  color: var(--ds-slate-900);
`;
const EtatPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  color: white;
  font-size: 12.5px;
  font-weight: 600;
  background: ${({ color }) => color};
`;
const Arret = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 10px;
  padding: 8px 12px;
  border-left: 3px solid ${({ color }) => color};
  background: ${({ color }) => `${color}14`};
  border-radius: 4px;
  font-size: 0.9rem;

  > .fa {
    margin-top: 3px;
    color: ${({ color }) => color};
  }
  > span {
    min-width: 0;
    word-break: break-word;
  }
`;
const Alerte = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-left: 3px solid ${({ color }) => color};
  background: ${({ color }) => `${color}14`};
  border-radius: 4px;
  font-size: 0.9rem;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  /* Action irréversible chez la compagnie : ambre foncé lisible, à la place du
     « warning » du thème (#ffcc99) trop clair pour du texte blanc. */
  .btn-agent-warning {
    color: #fff;
    background: #b45309;
    border-color: #b45309;
  }
  .btn-agent-warning:hover:not(:disabled),
  .btn-agent-warning:focus {
    color: #fff;
    background: #92400e;
    border-color: #92400e;
  }
  .btn-agent-warning:disabled {
    color: #fff;
    opacity: 0.65;
  }
  @media (max-width: 767px) {
    .btn {
      min-height: 44px;
    }
  }
`;
const Aide = styled.p`
  flex-basis: 100%;
  margin: 0;
  font-size: 12.5px;
  color: var(--ds-slate-500);
`;
const Groupe = styled.div`
  border: 1px solid var(--ds-slate-200);
  border-radius: 8px;
  margin-bottom: 10px;
`;
const TeteGroupe = styled.button`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 10px;
  width: 100%;
  min-height: 40px;
  padding: 6px 12px;
  border: 0;
  border-radius: 8px;
  background: var(--ds-slate-50);
  color: var(--ds-slate-700);
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background-color 150ms ease;

  &[aria-expanded="true"] {
    border-radius: 8px 8px 0 0;
  }
  > .fa:first-child {
    width: 12px;
    font-size: 11px;
    color: var(--ds-slate-500);
  }
  > strong {
    color: var(--ds-slate-900);
  }
  &:hover {
    background: var(--ds-slate-100);
  }
  &:focus-visible {
    outline: 2px solid var(--ds-blue);
    outline-offset: -2px;
  }
  @media (max-width: 767px) {
    min-height: 44px;
  }
`;
// L'issue d'un lancement : l'icône porte la couleur de l'état, le texte reste
// en encre foncée (les tons d'état clairs ne tiennent pas 4,5:1 sur le fond).
const Issue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  white-space: nowrap;
  font-weight: 600;
  color: var(--ds-slate-700);

  > .fa {
    color: ${({ color }) => color};
  }
`;
const Liste = styled.ol`
  list-style: none;
  margin: 0;
  padding: 8px 12px 4px;
`;
const Evenement = styled.li`
  display: grid;
  grid-template-columns: 60px 24px minmax(0, 1fr);
  column-gap: 8px;
  align-items: start;
  padding: 5px 0;
`;
const Heure = styled.span`
  font-size: 12px;
  line-height: 24px;
  color: var(--ds-slate-500);
  font-variant-numeric: tabular-nums;
`;
const Puce = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: white;
  background: ${({ color }) => color};
`;
const Corps = styled.div`
  min-width: 0;
  line-height: 24px;

  > strong {
    font-weight: 600;
  }
`;
const Detail = styled.div`
  color: var(--ds-slate-700);
  font-size: 0.9rem;
  line-height: 1.45;
  word-break: break-word;
`;
const Explication = styled.div`
  margin: 4px 0 2px;
  padding: 6px 10px;
  border-radius: 4px;
  background: ${({ color }) => `${color}14`};
  font-size: 0.9rem;
  line-height: 1.45;
`;

export default LeadAutoAgentTimeline;
